import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import PortalDocumentsPanel from "../../components/portal/PortalDocumentsPanel";
import { BoardCanvas } from "../../components/board/BoardCanvas";
import { BoardStrip } from "../../components/board/BoardStrip";
import { useBoardSync } from "../../lib/useBoardSync";
import { useBoards } from "../../lib/useBoards";
import { PortalInlineCount } from "../../components/PortalInventoryStat";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";

const SECTION_LABEL = "text-xs uppercase tracking-[0.16em] text-[var(--t-meta)]";

function SectionBar({ label, visible, total, subtitle, action }) {
  return (
    <div className="mb-3 flex min-w-0 items-center justify-between gap-3">
      <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <span className={SECTION_LABEL}>{label}</span>
        <PortalInlineCount visible={visible} total={total} />
        {subtitle ? (
          <span className="truncate text-xs text-[var(--t-meta)]">· {subtitle}</span>
        ) : null}
      </div>
      {action}
    </div>
  );
}

export default function ClientBoardPage({ embedded = false }) {
  const { user, client } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const clientId = client?.id;
  const isAdmin = user?.role === "admin";
  const [documentsRefreshKey, setDocumentsRefreshKey] = useState(0);
  const [createNoteOpen, setCreateNoteOpen] = useState(false);
  const [creatingBoard, setCreatingBoard] = useState(false);
  const [noteCounts, setNoteCounts] = useState({ visible: 0, total: 0 });
  const [editingBoardId, setEditingBoardId] = useState(null);
  const [pendingDeleteBoard, setPendingDeleteBoard] = useState(null);

  const activeBoardId = searchParams.get("board") || "";

  const { boards, loading: boardsLoading, createBoard, saveBoard, deleteBoard, load: reloadBoards } = useBoards(
    clientId,
    user,
  );

  const activeBoard = useMemo(
    () => boards.find((b) => b.id === activeBoardId) || null,
    [activeBoardId, boards],
  );

  const setActiveBoardId = useCallback(
    (boardId) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (boardId) next.set("board", boardId);
          else next.delete("board");
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  useEffect(() => {
    if (boardsLoading || !clientId) return;
    if (activeBoardId && boards.some((b) => b.id === activeBoardId)) return;
    if (boards.length > 0) {
      setActiveBoardId(boards[0].id);
      return;
    }
    if (activeBoardId) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("board");
          return next;
        },
        { replace: true },
      );
    }
  }, [activeBoardId, boards, boardsLoading, clientId, setActiveBoardId, setSearchParams]);

  const {
    elements,
    setElements,
    remoteCursors,
    connected,
    loading: boardLoading,
    sendCursor,
  } = useBoardSync(activeBoardId, clientId, user);

  const handleNotesCountsChange = useCallback((counts) => {
    setNoteCounts(counts);
  }, []);

  const handleAddBoard = useCallback(async () => {
    if (!clientId || creatingBoard) return;
    setCreatingBoard(true);
    try {
      const nextIndex = boards.length + 1;
      const board = await createBoard(`Board ${nextIndex}`);
      setActiveBoardId(board.id);
      setEditingBoardId(board.id);
    } catch {
      toast.error("Could not create board");
    } finally {
      setCreatingBoard(false);
    }
  }, [boards.length, clientId, createBoard, creatingBoard, setActiveBoardId]);

  const handleRenameBoard = useCallback(
    async (boardId, title) => {
      await saveBoard(boardId, { title });
      await reloadBoards();
    },
    [reloadBoards, saveBoard],
  );

  const requestDeleteBoard = useCallback((board) => {
    setPendingDeleteBoard(board);
  }, []);

  const confirmDeleteBoard = useCallback(async () => {
    const board = pendingDeleteBoard;
    if (!board) return;
    try {
      await deleteBoard(board.id);
      setPendingDeleteBoard(null);
      if (activeBoardId === board.id) {
        const remaining = boards.filter((b) => b.id !== board.id);
        setActiveBoardId(remaining[0]?.id || "");
      }
      toast.success("Board deleted");
    } catch {
      toast.error("Could not delete board");
    }
  }, [activeBoardId, boards, deleteBoard, pendingDeleteBoard, setActiveBoardId]);

  if (!clientId) {
    return (
      <div className="p-6 text-sm text-[var(--t-meta)]" data-testid="board-page">
        Select a client to open notes.
      </div>
    );
  }

  const addNoteButton = (
    <button
      type="button"
      onClick={() => setCreateNoteOpen(true)}
      className="inline-flex shrink-0 items-center gap-1 text-xs text-[var(--t-meta)] transition-colors hover:text-[var(--t-accent)]"
      data-testid="new-note-btn"
    >
      <Plus size={14} strokeWidth={1.4} /> Add note
    </button>
  );

  const addBoardButton = (
    <button
      type="button"
      onClick={handleAddBoard}
      disabled={creatingBoard}
      className="inline-flex shrink-0 items-center gap-1 text-xs text-[var(--t-meta)] transition-colors hover:text-[var(--t-accent)] disabled:opacity-50"
      data-testid="new-board-btn"
    >
      <Plus size={14} strokeWidth={1.4} /> {creatingBoard ? "Adding…" : "Add board"}
    </button>
  );

  const boardSubtitle =
    boards.length === 1 && activeBoard ? activeBoard.title || "Untitled board" : null;

  return (
    <div
      className={embedded ? "t-fade-up min-w-0" : "p-4 sm:p-5 md:p-7 t-fade-up min-w-0"}
      data-testid="board-page"
    >
      {!embedded ? (
        <header className="mb-8">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[var(--t-meta)]">Studio</p>
          <h1 className="mt-1 text-2xl font-light text-[var(--t-text)]">Notes</h1>
        </header>
      ) : null}

      <section className="mb-8 min-w-0" data-testid="notes-section">
        <SectionBar
          label="Notes"
          visible={noteCounts.visible}
          total={noteCounts.total}
          action={addNoteButton}
        />
        <PortalDocumentsPanel
          embedded
          layout="board"
          refreshKey={documentsRefreshKey}
          createOpen={createNoteOpen}
          onCreateOpenChange={setCreateNoteOpen}
          hideAddButton
          onNotesCountsChange={handleNotesCountsChange}
        />
      </section>

      <section className="min-w-0" data-testid="board-section">
        <SectionBar
          label="Board"
          visible={boards.length}
          total={boards.length}
          subtitle={boardSubtitle}
          action={addBoardButton}
        />
        {boards.length > 1 ? (
          <div className="mb-3 min-w-0">
            <BoardStrip
              boards={boards}
              loading={boardsLoading}
              activeBoardId={activeBoardId}
              onSelect={setActiveBoardId}
              editingBoardId={editingBoardId}
              onRenameBoard={handleRenameBoard}
              onEditComplete={() => setEditingBoardId(null)}
              onDeleteBoard={requestDeleteBoard}
              canDelete={isAdmin}
            />
          </div>
        ) : null}
        {activeBoardId ? (
          <BoardCanvas
            embedded={embedded}
            expanded
            elements={elements}
            setElements={setElements}
            remoteCursors={remoteCursors}
            sendCursor={sendCursor}
            connected={connected}
            loading={boardLoading}
            selfUserId={user?.id}
            clientId={clientId}
            clientName={client?.name}
            isAdmin={isAdmin}
            onNoteUpdated={() => setDocumentsRefreshKey((k) => k + 1)}
          />
        ) : null}
      </section>

      <AlertDialog open={pendingDeleteBoard !== null} onOpenChange={(open) => !open && setPendingDeleteBoard(null)}>
        <AlertDialogContent
          className="z-[80] max-w-md border border-[var(--t-border)] bg-[var(--t-bg)] text-[var(--t-text)] shadow-none sm:rounded-[var(--t-event-pill-radius)] gap-5"
          data-testid="board-delete-confirm-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-xl font-light tracking-tight text-[var(--t-text)]">
              Delete this board?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm font-light text-[var(--t-meta)]">
              {pendingDeleteBoard ? (
                <>
                  <span className="text-[var(--t-text)]">&ldquo;{pendingDeleteBoard.title || "Untitled board"}&rdquo;</span>{" "}
                  will be permanently removed. You cannot undo this.
                </>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-3 sm:justify-end">
            <AlertDialogCancel className="t-btn-secondary min-h-[44px] border-[var(--t-border)] bg-[var(--t-bg)] text-[var(--t-text)] hover:opacity-90">
              Cancel
            </AlertDialogCancel>
            <button
              type="button"
              className="neo-cta"
              data-testid="board-delete-confirm-btn"
              onClick={confirmDeleteBoard}
            >
              Delete
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
